import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { Spinner } from '@wordpress/components';

// Components.
import SafeHTML from '../safe-html';

// Services.
import getPostId from '../../js/services/getPostId';

// Styles.
import './jetpack-form.scss';

const JetpackForm = ({
  configUrl,
  formId,
  mediaUrl,
  nonce,
  postSubmit,
  submitUrl,
}) => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [values, setValues] = useState({});

  // Safely extract the config values.
  const {
    fields = [],
    header = 'Please tell us a bit about yourself.',
    message = 'Thank you for submitting your information.',
    skip_form: skipForm = 'No thanks, just take me to my download.',
    submit = 'Submit',
  } = config;

  // Load the form config on initial load.
  useEffect(() => {
    const loadConfig = async () => {
      const response = await fetch(configUrl);
      const responseJSON = await response.json();
      setConfig(responseJSON);
      setLoading(false);
    };
    loadConfig();
  }, []);

  // Set up a submit handler for the form.
  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    // Initialize the data object.
    const data = {
      form_id: formId,
      post_id: getPostId(),
      values: [],
    };

    // Build the values array.
    for (let i = 0; i < fields.length; i += 1) {
      data.values.push(values[i] || '');
    }

    // Send the values to the API.
    const response = await fetch(
      submitUrl,
      {
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        method: 'POST',
      }
    );

    await response.json();

    setSuccess(true);
    setSubmitting(false);

    // If a post-submit callback was provided, execute it.
    if (postSubmit) {
      postSubmit();
    }
  };

  // If we are loading, display the loading message.
  if (loading) {
    return (
      <p>Loading...</p>
    );
  }

  // If we have successfully submitted, say so.
  if (success) {
    return (
      <Fragment>
        <SafeHTML html={message} tag="p" />
        <Spinner />
      </Fragment>
    );
  }

  // Display the form.
  return (
    <Fragment>
      <h2 className="soft-gate-heading">{header}</h2>
      <form className="kauffman-jetpack-form" onSubmit={onSubmit}>
        {fields.map(({
          label, required, type, html,
        }, index) => {
          // NOTE: Else-if resolves Fragment/unique key issues.
          if (undefined !== html && 'paragraph' === type) {
            return (
              <SafeHTML
                className={classnames(
                  'kauffman-jetpack-form-field',
                  `kauffman-jetpack-form-field-${type}`
                )}
                html={html}
                key={`${html}-${type}`}
                tag="p"
              />
            );
          }

          return (
            <label
              className={classnames(
                'kauffman-jetpack-form-field',
                `kauffman-jetpack-form-field-${type}`
              )}
              htmlFor={`field-${index}`}
              key={label}
            >
              <SafeHTML html={label} tag="span" />
              {required ? (
                <span className="kauffman-jetpack-form-required">
                  <span className="screen-reader-text">required</span>
                </span>
              ) : null}
              <input
                defaultChecked={'checkbox' === type && values[index]}
                id={`field-${index}`}
                onChange={(event) => setValues({
                  ...values,
                  [index]: event.target.value,
                })}
                required={required}
                type={'name' === type ? 'text' : type}
                value={'checkbox' === type ? 'Yes' : values[index] || ''}
              />
            </label>
          );
        })}
        <input
          className="kauffman-jetpack-form-submit"
          disabled={submitting}
          type="submit"
          value={submit}
        />
      </form>
      <p className="soft-gate-skip-form">
        <a href={mediaUrl}>{skipForm}</a>
      </p>
    </Fragment>
  );
};

JetpackForm.defaultProps = {
  postSubmit: undefined,
};

JetpackForm.propTypes = {
  configUrl: PropTypes.string.isRequired,
  formId: PropTypes.number.isRequired,
  mediaUrl: PropTypes.string.isRequired,
  nonce: PropTypes.string.isRequired,
  postSubmit: PropTypes.func,
  submitUrl: PropTypes.string.isRequired,
};

export default JetpackForm;
